import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useManagerAuth } from '../../context/ManagerAuthContext';
import { 
  Activity, 
  Package, 
  Warehouse, 
  RefreshCw, 
  Search, 
  PlusCircle, 
  MinusCircle, 
  ArrowUpDown, 
  Clock, 
  ShieldCheck, 
  Layers, 
  List, 
  GitCommit, 
  TrendingUp, 
  TrendingDown
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const ACTION_CONFIG = {
  STOCK_ADDED: {
    label: 'Stock Added',
    bg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    icon: PlusCircle
  },
  STOCK_ADJUSTED: {
    label: 'Stock Adjusted',
    bg: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    icon: ArrowUpDown
  },
  STOCK_DAMAGED: {
    label: 'Stock Damaged',
    bg: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    icon: MinusCircle
  },
  TRANSFER_INITIATED: {
    label: 'Transfer Out',
    bg: 'bg-purple-50 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
    icon: ArrowUpDown
  },
  TRANSFER_RECEIVED: {
    label: 'Transfer In',
    bg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    icon: PlusCircle
  },
  INVENTORY_COUNT: {
    label: 'Stock Audit',
    bg: 'bg-amber-50 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    icon: ShieldCheck
  },
  DEFAULT: {
    label: 'System Action',
    bg: 'bg-slate-100 text-slate-700 border-slate-200',
    dot: 'bg-slate-500',
    icon: Activity
  }
};

const ManagerActivity = () => {
  const { getToken } = useManagerAuth();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' | 'table'

  const fetchActivity = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/manager/activity`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const result = await res.json();
      if (result.success) setActivity(result.activity || []);
    } catch (err) {
      console.error('Activity fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Filtered Activity List
  const filteredActivity = useMemo(() => {
    return activity.filter(item => {
      const matchesType = selectedType === 'ALL' || item.action_type === selectedType;
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || 
        (item.action_type && item.action_type.toLowerCase().includes(q)) ||
        (item.action_description && item.action_description.toLowerCase().includes(q)) ||
        (item.warehouse?.name && item.warehouse.name.toLowerCase().includes(q)) ||
        (item.warehouse?.code && item.warehouse.code.toLowerCase().includes(q)) ||
        (item.product?.name && item.product.name.toLowerCase().includes(q));

      return matchesType && matchesSearch;
    });
  }, [activity, selectedType, searchQuery]);

  // Telemetry Calculations
  const stats = useMemo(() => {
    const total = activity.length;
    let addedCount = 0;
    let addedQty = 0;
    let reducedCount = 0;
    let reducedQty = 0;

    activity.forEach(a => {
      if (a.quantity_affected > 0) {
        addedCount++;
        addedQty += a.quantity_affected;
      } else if (a.quantity_affected < 0) {
        reducedCount++;
        reducedQty += Math.abs(a.quantity_affected);
      }
    });

    return { total, addedCount, addedQty, reducedCount, reducedQty };
  }, [activity]);

  // Unique Action Types for Pills
  const actionTypes = useMemo(() => {
    const types = Array.from(new Set(activity.map(a => a.action_type).filter(Boolean)));
    return ['ALL', ...types];
  }, [activity]);

  return (
    <div className="space-y-6 select-none font-sans pb-12">
      {/* ── 1. HERO EXECUTIVE WELCOME HEADER ── */}
      <div className="relative bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 rounded-3xl p-6 sm:p-8 text-white border border-purple-800 shadow-2xl overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="p-2 rounded-xl bg-purple-500/30 text-purple-300 border border-purple-400/40">
                <Activity size={22} className="animate-pulse" />
              </span>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white uppercase italic">
                Activity & Audit Log
              </h1>
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                Live Log Stream
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-300 font-medium max-w-xl">
              Audit log of stock modifications, inbound entries, dispatches, and inventory adjustments across your assigned hubs.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={fetchActivity}
              disabled={loading}
              className="px-4 py-2.5 bg-slate-800/90 hover:bg-slate-700 border border-slate-600 rounded-2xl text-xs font-black text-slate-200 hover:text-white transition-all shadow-md flex items-center gap-2 active:scale-95 disabled:opacity-50"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin text-purple-400' : 'text-slate-400'} />
              <span>Refresh Feed</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. TELEMETRY KPI CARDS (Light Theme) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600">
              <Layers className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-200">
              TOTAL LOGS
            </span>
          </div>
          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">{stats.total}</p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">Recorded Events</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Session audit trail</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm hover:border-emerald-400 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
              ADDITIONS
            </span>
          </div>
          <div>
            <p className="text-3xl font-black text-emerald-600 font-mono tracking-tight flex items-baseline gap-2">
              <span>{stats.addedCount}</span>
              <span className="text-xs font-bold text-emerald-700">(+{stats.addedQty} units)</span>
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">Stock Added</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Inbound & restock</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm hover:border-rose-400 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600">
              <TrendingDown className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200">
              REDUCTIONS
            </span>
          </div>
          <div>
            <p className="text-3xl font-black text-rose-600 font-mono tracking-tight flex items-baseline gap-2">
              <span>{stats.reducedCount}</span>
              <span className="text-xs font-bold text-rose-700">(-{stats.reducedQty} units)</span>
            </p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">Stock Reduced</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Dispatches & damage</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm hover:border-amber-400 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-800 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
              INTEGRITY
            </span>
          </div>
          <div>
            <p className="text-3xl font-black text-slate-900 font-mono tracking-tight">100%</p>
            <p className="text-xs font-black uppercase tracking-wider text-slate-700 mt-1">Verified Audit</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Immutable record log</p>
          </div>
        </div>
      </div>

      {/* ── 3. SEARCH, ACTION FILTER PILLS & VIEW TOGGLE ── */}
      <div className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by action, product, warehouse or detail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:bg-white transition-all"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-200 px-2 py-0.5 rounded-lg"
            >
              Clear
            </button>
          )}
        </div>

        {/* Action Type Pills */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200 overflow-x-auto max-w-full">
            {actionTypes.map(type => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  selectedType === type
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                }`}
              >
                {type === 'ALL' ? 'All Events' : type.replace(/_/g, ' ')}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200">
            <button
              onClick={() => setViewMode('timeline')}
              className={`p-2 rounded-xl transition-all ${
                viewMode === 'timeline'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              title="Timeline View"
            >
              <GitCommit size={16} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-xl transition-all ${
                viewMode === 'table'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              title="Table View"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ── 4. MAIN FEED CONTENT ── */}
      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-16 text-center shadow-sm">
          <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 font-bold text-sm">Loading activity logs...</p>
        </div>
      ) : filteredActivity.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-16 text-center shadow-sm">
          <Activity size={40} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-base font-bold text-slate-800">No Activity Found</h3>
          <p className="text-slate-500 text-xs mt-1">No log entries match your search criteria or selected filter.</p>
          {(searchQuery || selectedType !== 'ALL') && (
            <button
              onClick={() => { setSearchQuery(''); setSelectedType('ALL'); }}
              className="mt-4 px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl text-xs font-bold hover:bg-purple-100 transition-all"
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : viewMode === 'timeline' ? (
        /* TIMELINE VIEW */
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm relative">
          <div className="relative pl-6 md:pl-10 space-y-6 before:absolute before:left-3 md:before:left-5 before:top-3 before:bottom-3 before:w-0.5 before:bg-slate-200">
            {filteredActivity.map((a, idx) => {
              const cfg = ACTION_CONFIG[a.action_type] || ACTION_CONFIG.DEFAULT;
              const IconComp = cfg.icon;

              return (
                <div key={a.id || idx} className="relative group">
                  {/* Timeline Circle Node */}
                  <div className={`absolute -left-6 md:-left-10 top-2 w-6 h-6 rounded-full bg-white border-2 border-slate-300 group-hover:border-purple-600 flex items-center justify-center shadow-sm z-10 transition-colors`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                  </div>

                  {/* Activity Card */}
                  <div className="bg-slate-50/70 hover:bg-purple-50/30 border border-slate-200/80 hover:border-purple-300 rounded-2xl p-4 sm:p-5 transition-all shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200/60 pb-3 mb-3">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        {/* Action Badge */}
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider border ${cfg.bg}`}>
                          <IconComp size={14} />
                          {cfg.label}
                        </span>

                        {/* Warehouse Tag */}
                        {a.warehouse && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 shadow-2xs">
                            <Warehouse size={13} className="text-purple-600" />
                            {a.warehouse.code || a.warehouse.name}
                          </span>
                        )}

                        {/* Product Tag */}
                        {a.product && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 shadow-2xs truncate max-w-[220px]">
                            <Package size={13} className="text-amber-600" />
                            {a.product.name}
                          </span>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono font-medium">
                        <Clock size={13} className="text-slate-400" />
                        <span>
                          {new Date(a.performed_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span>
                          {new Date(a.performed_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Description & Impact */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                        {a.action_description || 'Action completed successfully.'}
                      </p>

                      {a.quantity_affected != null && (
                        <div className="shrink-0 self-start sm:self-center">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-black font-mono border ${
                            a.quantity_affected > 0
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : a.quantity_affected < 0
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            {a.quantity_affected > 0 ? '+' : ''}{a.quantity_affected} Units
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-black text-slate-500 uppercase tracking-wider">
                  <th className="px-5 py-4">Action Type</th>
                  <th className="px-5 py-4">Warehouse Hub</th>
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">Description</th>
                  <th className="px-5 py-4 text-center">Variance</th>
                  <th className="px-5 py-4 text-right">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredActivity.map((a, idx) => {
                  const cfg = ACTION_CONFIG[a.action_type] || ACTION_CONFIG.DEFAULT;
                  const IconComp = cfg.icon;

                  return (
                    <tr key={a.id || idx} className="hover:bg-slate-50/80 transition-colors">
                      {/* Action Type */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black uppercase border ${cfg.bg}`}>
                          <IconComp size={13} />
                          {cfg.label}
                        </span>
                      </td>

                      {/* Warehouse */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="font-bold text-slate-900">
                          {a.warehouse?.code || '—'}
                        </div>
                        {a.warehouse?.name && (
                          <div className="text-xs text-slate-500 truncate max-w-[150px]">
                            {a.warehouse.name}
                          </div>
                        )}
                      </td>

                      {/* Product */}
                      <td className="px-5 py-4">
                        {a.product ? (
                          <div className="font-bold text-slate-900 truncate max-w-[180px]">
                            {a.product.name}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Description */}
                      <td className="px-5 py-4">
                        <div className="text-slate-600 max-w-xs text-xs font-medium leading-relaxed">
                          {a.action_description || '—'}
                        </div>
                      </td>

                      {/* Quantity */}
                      <td className="px-5 py-4 text-center whitespace-nowrap font-mono">
                        {a.quantity_affected != null ? (
                          <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-xs font-black border ${
                            a.quantity_affected > 0
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : a.quantity_affected < 0
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            {a.quantity_affected > 0 ? '+' : ''}{a.quantity_affected}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Date & Time */}
                      <td className="px-5 py-4 text-right whitespace-nowrap text-xs font-mono">
                        <div className="text-slate-900 font-bold">
                          {new Date(a.performed_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-slate-400 text-[11px]">
                          {new Date(a.performed_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerActivity;


